'use client';

import * as React from 'react';
import { ComponentPropsWithoutRef } from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { Slot } from '@radix-ui/react-slot';
import { CheckSmallIcon } from '@zep-us/zus-icons';
import { VariantProps } from 'class-variance-authority';
import { ChevronRight } from 'lucide-react';
import { cn } from '@zep/utils';
import { MENU_CHECKBOX_ITEM, MENU_CONTENT, MENU_ICON_WRAPPER, MENU_ITEM_LABEL, MENU_ITEMINDICATOR_WRAPPER, MENU_LABEL, MENU_RADIO_ITEM, MENU_SEPARATOR, MENU_SHORTCUT, MENU_SUBCONTENT, MENU_SUBTRIGGER, menuItemVariants } from './constants';
const DropdownMenu = DropdownMenuPrimitive.Root;
const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
const DropdownMenuGroup = DropdownMenuPrimitive.Group;
const DropdownMenuPortal = DropdownMenuPrimitive.Portal;
const DropdownMenuSub = DropdownMenuPrimitive.Sub;
const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;
const DropdownMenuSubTrigger = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>, React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
  inset?: boolean;
}>(({
  className,
  inset,
  children,
  ...props
}, ref) => <DropdownMenuPrimitive.SubTrigger ref={ref} className={cn(MENU_SUBTRIGGER, inset && 'pl-8', className)} {...props}>
    {children}
    <ChevronRight className="ml-auto size-4" />
  </DropdownMenuPrimitive.SubTrigger>);
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName;
const DropdownMenuSubContent = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitive.SubContent>, React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>>(({
  className,
  ...props
}, ref) => <DropdownMenuPrimitive.SubContent ref={ref} className={cn(MENU_SUBCONTENT, className)} {...props} />);
DropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName;
const DropdownMenuContent = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitive.Content>, React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>>(({
  className,
  sideOffset = 4,
  ...props
}, ref) => <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.Content ref={ref} sideOffset={sideOffset} className={cn(MENU_CONTENT, className)} {...props} />
  </DropdownMenuPrimitive.Portal>);
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;
const DropdownMenuItem = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitive.Item>, React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & VariantProps<typeof menuItemVariants> & {
  inset?: boolean;
}>(({
  className,
  color,
  inset,
  ...props
}, ref) => <DropdownMenuPrimitive.Item ref={ref} className={cn(menuItemVariants({
  color
}), inset && 'pl-8', className)} {...props} />);
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;
type DropdownMenuItemLabelProps = ComponentPropsWithoutRef<'span'>;
const DropdownMenuItemLabel = (props: DropdownMenuItemLabelProps) => {
  const {
    className,
    children,
    ...rest
  } = props;
  return <span className={cn(MENU_ITEM_LABEL, className)} {...rest} data-sentry-component="DropdownMenuItemLabel" data-sentry-source-file="DropdownMenu.tsx">
      {children}
    </span>;
};
const DropdownMenuCheckboxItem = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>, React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>>(({
  className,
  children,
  checked,
  ...props
}, ref) => <DropdownMenuPrimitive.CheckboxItem ref={ref} className={cn(MENU_CHECKBOX_ITEM, className)} checked={checked} {...props}>
    <div className="flex flex-1 truncate">{children}</div>
    <DropdownMenuPrimitive.ItemIndicator>
      <span className={cn(MENU_ITEMINDICATOR_WRAPPER)}>
        <CheckSmallIcon className="size-[18px] fill-current" />
      </span>
    </DropdownMenuPrimitive.ItemIndicator>
  </DropdownMenuPrimitive.CheckboxItem>);
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName;
const DropdownMenuRadioItem = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>, React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>>(({
  className,
  children,
  ...props
}, ref) => <DropdownMenuPrimitive.RadioItem ref={ref} className={cn(MENU_RADIO_ITEM, className)} {...props}>
    {children}
    <DropdownMenuPrimitive.ItemIndicator>
      <span className={cn(MENU_ITEMINDICATOR_WRAPPER)}>
        <CheckSmallIcon className="size-[18px] fill-current" />
      </span>
    </DropdownMenuPrimitive.ItemIndicator>
  </DropdownMenuPrimitive.RadioItem>);
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;
const DropdownMenuLabel = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitive.Label>, React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
  inset?: boolean;
}>(({
  className,
  inset,
  ...props
}, ref) => <DropdownMenuPrimitive.Label ref={ref} className={cn(MENU_LABEL, inset && 'pl-8', className)} {...props} />);
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;
const DropdownMenuSeparator = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitive.Separator>, React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>>(({
  className,
  ...props
}, ref) => <DropdownMenuPrimitive.Separator ref={ref} className={cn(MENU_SEPARATOR, className)} {...props} />);
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;
const DropdownMenuShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return <span className={cn(MENU_SHORTCUT, className)} {...props} data-sentry-component="DropdownMenuShortcut" data-sentry-source-file="DropdownMenu.tsx" />;
};
DropdownMenuShortcut.displayName = 'DropdownMenuShortcut';
const DropdownMenuIconWrapper = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<'div'> & {
  asChild?: boolean;
}>(({
  className,
  asChild = false,
  ...props
}, ref) => {
  const Comp = asChild ? Slot : 'div';
  return <Comp className={cn(MENU_ICON_WRAPPER, className)} ref={ref} {...props} />;
});
DropdownMenuIconWrapper.displayName = 'DropdownMenuIconWrapper';
export { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuItemLabel, DropdownMenuCheckboxItem, DropdownMenuRadioItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuGroup, DropdownMenuPortal, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuRadioGroup, DropdownMenuIconWrapper };